import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import store from "../store";
import { setUser as setUserRedux, clearUser } from "../store/userSlice";
import { sendOtp } from "../services/sendOtpService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { signupUser } from "../services/userService";
import secureStorage from "../utils/secureStorage";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = secureStorage.getJSON("user");
    const isLoggedIn = secureStorage.getItem("isLoggedIn");
    const accessToken = secureStorage.getItem("accessToken");

    if (storedUser && isLoggedIn === "true") {
      setUser(storedUser);
      setIsAuthenticated(true);
      // store.dispatch(setUserRedux(storedUser));
    }
    setLoading(false);
  }, []);

  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://pro.vibelinksapp.com/api";

  const login = async (email, password) => {
    try {
      const { getCurrentFCMToken } = await import("../utils/fcmToken");
      const fcmToken = await getCurrentFCMToken();
      
      const headers = {};
      if (fcmToken) {
        headers["FCM-Token"] = fcmToken;
      }
      
      const response = await axios.post(
        `${BASE_URL}/login`,
        { email, password },
        { headers }
      );

      const resData = response.data;

      if (resData.success && resData.data) {
        const userData = {
          ...resData.data,
          id: resData.data.id,
          full_name: resData.data.full_name,
          email: resData.data.email,
          image: resData.data.image,
          access_token: resData.data.access_token,
          subscription_details: resData.data.subscription_details,
          followerStatus: resData.data.follower_status,
          addresses: resData.data.addresses,
        };

        setUser(userData);
        setIsAuthenticated(true);
        secureStorage.setItem("accessToken", resData.data.access_token);
        secureStorage.setJSON("user", userData);
        secureStorage.setItem("isLoggedIn", "true");
        store.dispatch(setUserRedux(userData));
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  const signup = async (formData) => {
    try {
      const response = await signupUser(formData);

      if (!response.success) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Signup failed");
      }
      const resData = response;

      if (resData.success && resData.data) {
        const userData = {
          ...resData.data,
        };
        if (userData.email_token) {
          secureStorage.setItem("email_token", userData.email_token);
        }
        setUser(userData);
        setIsAuthenticated(true);
        secureStorage.setJSON("user", userData);
        secureStorage.setItem("accessToken", userData.access_token);
        secureStorage.setItem("isLoggedIn", "true");
        store.dispatch(setUserRedux(userData));
        // toast.success(resData.message || 'Signup successful!');
        if (userData.is_email_verified === false) {
          const sendOptResponse = await sendOtp(userData.email, 2);
          if (!sendOptResponse.success) {
            toast.error(sendOptResponse.message || "Failed to send OTP");
          }
          secureStorage.setItem(
            "email_token",
            sendOptResponse?.data.email_token
          );

          // Set timers: 60 seconds for OTP validity, resend timer based on response or default
          const otpDuration = 60 * 1000; // 60 seconds for OTP validity
          const resendDuration = sendOptResponse?.data?.email_resend_time
            ? sendOptResponse.data.email_resend_time * 1000
            : 60 * 1000; // Default to 60 seconds if not provided
          secureStorage.setItem("otp_expiration", String(Date.now() + otpDuration));
          secureStorage.setItem(
            "resend_expiration",
            String(Date.now() + resendDuration)
          );
          navigate("/otp?type=verify_email");
        }
        return true;
      }
      toast.error(resData.message || "Signup failed!");
      return false;
    } catch (error) {
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    secureStorage.removeItem("user");
    secureStorage.removeItem("isLoggedIn");
    secureStorage.removeItem("accessToken");
    store.dispatch(clearUser());
  };

  const value = {
    user,
    isAuthenticated,
    login,
    logout,
    signup,
    setIsAuthenticated,
    setUser,
    updateUser: (partialData) => {
      setUser((prev) => {
        const updated = { ...prev, ...partialData };
        try {
          secureStorage.setJSON("user", updated);
        } catch (e) {
        }
        store.dispatch(setUserRedux(updated));
        return updated;
      });
    },
  };

  if (loading) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
